import { coerceBooleanProperty } from '@angular/cdk/coercion';
import * as i0 from '@angular/core';
import { inject, ElementRef, Directive, Input, HostBinding, ChangeDetectorRef, ContentChildren, ContentChild, NgModule } from '@angular/core';
import { ResizeObserverService } from '@simpl/element-ng/resize-observer';
import { map, distinctUntilChanged, shareReplay, startWith, switchMap, auditTime } from 'rxjs/operators';
import { of, combineLatest } from 'rxjs';
import { CommonModule } from '@angular/common';

class SiAutoCollapsableListMeasurable {
    size$;
    elementRef = inject((ElementRef));
    resizeObserverService = inject(ResizeObserverService);
    ngAfterViewInit() {
        this.size$ = this.resizeObserverService
            .observe(this.elementRef.nativeElement, 0, true, true)
            .pipe(map(size => size.width), distinctUntilChanged(), shareReplay(1));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAutoCollapsableListMeasurable, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiAutoCollapsableListMeasurable, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAutoCollapsableListMeasurable, decorators: [{
            type: Directive
        }] });

class SiAutoCollapsableListItemDirective extends SiAutoCollapsableListMeasurable {
    /**
     * Hide this item even if enough space is available.
     * When calculating the overall available size, this item is still considered when forceHide=true
     */
    set forceHide(value) {
        this._forceHide = coerceBooleanProperty(value);
    }
    get forceHide() {
        return this._forceHide;
    }
    /**
     * True if enough space is available for this item.
     */
    canBeVisible;
    _forceHide = false;
    get visibility() {
        return this.isVisible ? 'visible' : 'hidden';
    }
    get position() {
        return this.isVisible ? '' : 'absolute';
    }
    // Ensure hidden items are behind the visible ones. Otherwise, the visible ones are not clickable
    get zIndex() {
        return this.isVisible ? '' : '-1';
    }
    /**
     * True if this item is actually visible to the user
     */
    get isVisible() {
        return !!this.canBeVisible && !this._forceHide;
    }
    ngAfterViewInit() {
        if (getComputedStyle(this.elementRef.nativeElement).display === 'inline') {
            console.error('siAutoCollapsibleListItem does not work on items with display="inline"');
        }
        super.ngAfterViewInit();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAutoCollapsableListItemDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiAutoCollapsableListItemDirective, selector: "[siAutoCollapsableListItem]", inputs: { forceHide: "forceHide" }, host: { properties: { "style.visibility": "this.visibility", "style.position": "this.position", "style.z-index": "this.zIndex" } }, exportAs: ["siAutoCollapsableListItem"], usesInheritance: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAutoCollapsableListItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siAutoCollapsableListItem]',
                    exportAs: 'siAutoCollapsableListItem'
                }]
        }], propDecorators: { forceHide: [{
                type: Input
            }], visibility: [{
                type: HostBinding,
                args: ['style.visibility']
            }], position: [{
                type: HostBinding,
                args: ['style.position']
            }], zIndex: [{
                type: HostBinding,
                args: ['style.z-index']
            }] } });

class SiAutoCollapsableListAdditionalContentDirective extends SiAutoCollapsableListMeasurable {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAutoCollapsableListAdditionalContentDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiAutoCollapsableListAdditionalContentDirective, selector: "[siAutoCollapsableListAdditionalContent]", usesInheritance: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAutoCollapsableListAdditionalContentDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siAutoCollapsableListAdditionalContent]'
                }]
        }] });

class SiAutoCollapsableListOverflowItemDirective extends SiAutoCollapsableListItemDirective {
    hiddenItemCount = 0;
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAutoCollapsableListOverflowItemDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiAutoCollapsableListOverflowItemDirective, selector: "[siAutoCollapsableListOverflowItem]", exportAs: ["siAutoCollapsableListOverflowItem"], usesInheritance: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAutoCollapsableListOverflowItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siAutoCollapsableListOverflowItem]',
                    exportAs: 'siAutoCollapsableListOverflowItem'
                }]
        }] });

class SiAutoCollapsableListDirective {
    items;
    overflowItem;
    additionalContent;
    get overflow() {
        return this._siAutoCollapsableList ? 'hidden' : '';
    }
    _siAutoCollapsableList = true;
    get siAutoCollapsableList() {
        return this._siAutoCollapsableList;
    }
    /** @defaultref {@link _siAutoCollapsableList} */
    set siAutoCollapsableList(value) {
        this._siAutoCollapsableList = coerceBooleanProperty(value);
        if (!this._siAutoCollapsableList && this.resizeSubscription) {
            this.reset();
        }
        else if (this._siAutoCollapsableList && !this.resizeSubscription && this.items) {
            this.setupResizeListener();
        }
    }
    resizeSubscription;
    disableInitSubscription;
    elementRef = inject(ElementRef);
    resizeObserverService = inject(ResizeObserverService);
    changeDetectorRef = inject(ChangeDetectorRef);
    ngAfterViewInit() {
        if (this.siAutoCollapsableList) {
            this.setupResizeListener();
        }
        else {
            this.reset();
        }
    }
    ngOnDestroy() {
        this.resizeSubscription?.unsubscribe();
        this.disableInitSubscription?.unsubscribe();
    }
    setupResizeListener() {
        this.disableInitSubscription?.unsubscribe();
        const containerSize$ = this.resizeObserverService
            .observe(this.elementRef.nativeElement, 0, true, true)
            .pipe(map(size => size.width), distinctUntilChanged());
        const itemSizes$ = this.items.changes.pipe(startWith(this.items), switchMap((items) => !items.length
            ? of([])
            : combineLatest(items.map(item => item.size$.pipe(map(size => ({
                size,
                directive: item
            })))))));
        const additionalContentSizes$ = this.additionalContent.changes.pipe(startWith(this.additionalContent), switchMap((additionalContent) => combineLatest(additionalContent.map(item => item.size$))), startWith([]));
        this.resizeSubscription = combineLatest([
            containerSize$,
            this.overflowItem?.size$ ?? of(0),
            itemSizes$,
            additionalContentSizes$
        ])
            .pipe(auditTime(0))
            .subscribe(([containerSize, overflowItemSize, items, additionalContentSizes]) => this.updateItemVisibility(containerSize, overflowItemSize, items, additionalContentSizes));
    }
    updateItemVisibility(size, overflowSize, items, additionalContent) {
        let remainingSpace = size - additionalContent.reduce((a, b) => a + b, 0);
        const itemsCopy = items.slice();
        while (remainingSpace > 0 && itemsCopy.length) {
            const item = itemsCopy.shift();
            if (remainingSpace - item.size - overflowSize >= 0) {
                item.directive.canBeVisible = true;
                remainingSpace -= item.size;
            }
            else if (!itemsCopy.length &&
                (remainingSpace - item.size >= 0 || overflowSize >= item.size)) {
                item.directive.canBeVisible = true;
                remainingSpace = 0;
            }
            else {
                remainingSpace = 0;
                item.directive.canBeVisible = false;
            }
        }
        itemsCopy.forEach(item => (item.directive.canBeVisible = false));
        if (this.overflowItem) {
            this.overflowItem.hiddenItemCount = this.items.filter(item => !item.canBeVisible).length;
            this.overflowItem.canBeVisible = this.overflowItem.hiddenItemCount !== 0;
        }
        this.changeDetectorRef.markForCheck();
    }
    reset() {
        this.resizeSubscription?.unsubscribe();
        this.resizeSubscription = undefined;
        this.disableInitSubscription = this.items.changes.subscribe((items) => queueMicrotask(() => {
            items.forEach(item => (item.canBeVisible = true));
            this.changeDetectorRef.markForCheck();
        }));
        this.items.notifyOnChanges();
        if (this.overflowItem) {
            queueMicrotask(() => {
                this.overflowItem.canBeVisible = false;
                this.overflowItem.hiddenItemCount = 0;
                this.changeDetectorRef.markForCheck();
            });
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAutoCollapsableListDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiAutoCollapsableListDirective, selector: "[siAutoCollapsableList]", inputs: { siAutoCollapsableList: "siAutoCollapsableList" }, host: { properties: { "style.overflow": "this.overflow" } }, queries: [{ propertyName: "overflowItem", first: true, predicate: SiAutoCollapsableListOverflowItemDirective, descendants: true }, { propertyName: "items", predicate: SiAutoCollapsableListItemDirective }, { propertyName: "additionalContent", predicate: SiAutoCollapsableListAdditionalContentDirective }], exportAs: ["siAutoCollapsableList"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAutoCollapsableListDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siAutoCollapsableList]',
                    exportAs: 'siAutoCollapsableList'
                }]
        }], propDecorators: { items: [{
                type: ContentChildren,
                args: [SiAutoCollapsableListItemDirective]
            }], overflowItem: [{
                type: ContentChild,
                args: [SiAutoCollapsableListOverflowItemDirective]
            }], additionalContent: [{
                type: ContentChildren,
                args: [SiAutoCollapsableListAdditionalContentDirective]
            }], overflow: [{
                type: HostBinding,
                args: ['style.overflow']
            }], siAutoCollapsableList: [{
                type: Input
            }] } });

class SiAutoCollapsableListModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAutoCollapsableListModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiAutoCollapsableListModule, declarations: [SiAutoCollapsableListAdditionalContentDirective,
            SiAutoCollapsableListDirective,
            SiAutoCollapsableListItemDirective,
            SiAutoCollapsableListOverflowItemDirective], imports: [CommonModule], exports: [SiAutoCollapsableListAdditionalContentDirective,
            SiAutoCollapsableListDirective,
            SiAutoCollapsableListItemDirective,
            SiAutoCollapsableListOverflowItemDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAutoCollapsableListModule, imports: [CommonModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiAutoCollapsableListModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        SiAutoCollapsableListAdditionalContentDirective,
                        SiAutoCollapsableListDirective,
                        SiAutoCollapsableListItemDirective,
                        SiAutoCollapsableListOverflowItemDirective
                    ],
                    imports: [CommonModule],
                    exports: [
                        SiAutoCollapsableListAdditionalContentDirective,
                        SiAutoCollapsableListDirective,
                        SiAutoCollapsableListItemDirective,
                        SiAutoCollapsableListOverflowItemDirective
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SiAutoCollapsableListAdditionalContentDirective, SiAutoCollapsableListDirective, SiAutoCollapsableListItemDirective, SiAutoCollapsableListMeasurable, SiAutoCollapsableListModule, SiAutoCollapsableListOverflowItemDirective };
